import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { Modal } from '../../../../../components/Modal/Modal'
import { AiExecuteResult, AiResult } from '../../../../../models/AiResult'
import { selectAiResultRerunLoading, selectAiResultSaveLoading } from '../../../../../store/meetingSummary/aiResults'
import { useOverrideAiResult, useRerunAiFeature, useSaveAiResult } from '../hooks/api'
import { TrackEvent, useTracking } from '../../common/tracking'
import { AiFeature } from '../../../../../models/AiFeature'
import { selectSelectedMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { Button } from '../../../../../components/Button/Button'
import { getRoomAndAIFeatureTrackingDetails } from '../../tracking'
import { RerunPlaceholder } from './RerunPlaceholder'
import { NoMeetingResult } from '../MeetingResults/DefaultDisplay/View/NoMeetingResult'
import { DefaultMeetingResultEditView } from '../MeetingResults/DefaultDisplay/DefaultMeetingResultEditView'
import { nanoid } from 'nanoid'
import { replaceFormattedResultOnPath, replaceResultOnPath } from '../MeetingResults/DefaultDisplay/utils'
import { copyResult, HTML_TYPE, writeCopyContentToClipboard } from '../copy'
import { showToast } from '../../../../../store/meetingSummary/display'
import { SplitButton } from '../../../../../components/SplitButton/SplitButton'
import { useMeetingSummary } from '../MeetingSummaryAgent/hooks'
import { removeTopLevelEmptyParagraphs } from '../MeetingResults/Lexical/utils'
import './RerunModal.scss'

export interface RerunModalProps {
  aiFeature: AiFeature
  aiResult: AiResult
  show: boolean
  onHide: () => void
}

export const RerunModal: React.FC<RerunModalProps> = ({ aiFeature, aiResult, show, onHide }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const roomSession = useAppSelector(selectRoomSession)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const isRerunLoading = useAppSelector(selectAiResultRerunLoading)
  const isSaveLoading = useAppSelector(selectAiResultSaveLoading)

  const [newAiResult, setNewAiResult] = useState<AiResult | null>(null)

  const rerunAiFeature = useRerunAiFeature()
  const saveAiResult = useSaveAiResult()
  const overrideAiResult = useOverrideAiResult()
  const { handleMeetingSummaryResultEdited } = useMeetingSummary()

  const transformExecuteResult = useCallback((roomId: string, sessionId: string, executeResult: AiExecuteResult): AiResult => {
    const { aiFeatureId, result: aiFeatureResult, inputParametersHierarchyId, userId } = executeResult

    return {
      id: nanoid(),
      roomId,
      sessionId,
      userId,
      inputParametersHierarchyId,
      result: aiFeatureResult,
      aiFeatureId,
      status: 'full_context',
      createdAt: new Date().toISOString(),
      scores: [],
      syncStatus: [],
    }
  }, [])

  const triggerRerunAiFeature = useCallback(async () => {
    if (!selectedMeetingTemplate || !roomSession) {
      return
    }
    setNewAiResult(null)
    const { roomId, id: sessionId } = roomSession
    const { id: aiFeatureId } = aiFeature
    const { id: meetingTemplateId } = selectedMeetingTemplate
    const result = await rerunAiFeature(aiFeatureId, meetingTemplateId)
    if (!result?.length) {
      return
    }
    setNewAiResult(transformExecuteResult(roomId, sessionId, result[0]))
  }, [selectedMeetingTemplate, roomSession, aiFeature, rerunAiFeature, transformExecuteResult])

  useEffect(() => {
    triggerRerunAiFeature()
  }, [triggerRerunAiFeature])

  const handleResultTextContentChanged = useCallback((basePath: string, path: string, changedResult: string | boolean) => {
    setNewAiResult((newAiResult) => {
      if (!newAiResult) {
        return newAiResult
      }
      const newResult = replaceResultOnPath(newAiResult, basePath, path, changedResult)
      return { ...newAiResult, result: newResult }
    })
  }, [])

  const handleFormattedResultChanged = useCallback((basePath: string, path: string, contentType: string, content: string) => {
    setNewAiResult((newAiResult) => {
      if (!newAiResult) {
        return newAiResult
      }
      const newResult = replaceFormattedResultOnPath(newAiResult, basePath, path, contentType, content)
      return { ...newAiResult, result: newResult }
    })
  }, [])

  const handleCopyClicked = async () => {
    if (!newAiResult) {
      return
    }
    const copiedResult = copyResult(aiFeature, newAiResult, { roomSessionStartedAt: roomSession?.startedAt })
    copiedResult[HTML_TYPE] = removeTopLevelEmptyParagraphs(copiedResult[HTML_TYPE])
    await writeCopyContentToClipboard(copiedResult)
    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Copied to clipboard!' }))
  }

  const rerunModalBody = useMemo(() => {
    return (
      <div className='d-flex flex-column'>
        <h4 className='zocks-mb-2 text-center'>Rerun ‘{aiFeature.name}’</h4>
        {isRerunLoading ? (
          <RerunPlaceholder />
        ) : (
          <>
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex align-items-center justify-content-between'>
                <h6>{aiFeature.name}</h6>
                <Button className='ms-auto button-small px-2' variant='borderless' disabled={isRerunLoading || isSaveLoading} onClick={triggerRerunAiFeature}>
                  <i className='bi bi-arrow-clockwise fs-6' />
                  Rerun
                </Button>
              </div>
              {!newAiResult ? (
                <NoMeetingResult />
              ) : (
                <>
                  <div className='default-edit-result-container p-3 mb-4'>
                    <DefaultMeetingResultEditView
                      aiFeature={aiFeature}
                      aiResult={newAiResult}
                      onResultTextContentChange={handleResultTextContentChanged}
                      onFormattedResultChange={handleFormattedResultChanged}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    )
  }, [aiFeature, isRerunLoading, isSaveLoading, triggerRerunAiFeature, newAiResult, handleResultTextContentChanged, handleFormattedResultChanged])

  if (!roomSession || !selectedMeetingTemplate) {
    return null
  }

  const _handleSaveAiResult = async () => {
    if (!newAiResult) {
      return
    }

    const { roomId, id: sessionId } = roomSession
    const { id: aiFeatureId } = aiFeature
    const { result, inputParametersHierarchyId } = newAiResult

    await saveAiResult({ roomId, sessionId, aiFeatureId, inputParametersHierarchyId, result })
    onHide()
  }

  const handleSaveAiResult = tracked(_handleSaveAiResult, {
    eventName: TrackEvent.RERUN_AI_RESULT_SAVED,
    eventDetails: {
      ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
    },
  })

  const _handleOverrideAiResult = async () => {
    if (!newAiResult) {
      return
    }

    const { id: aiResultId } = aiResult
    const { result } = newAiResult

    await overrideAiResult(aiResultId, { result }, () => handleMeetingSummaryResultEdited(aiFeature, aiResultId, result))
    onHide()
  }

  const handleOverrideAiResult = tracked(_handleOverrideAiResult, {
    eventName: TrackEvent.RERUN_AI_RESULT_REPLACED,
    eventDetails: {
      ...getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
    },
  })

  const rerunModalFooter = (
    <div className='d-flex flex-grow-1 align-items-center justify-content-between'>
      {newAiResult ? (
        <Button variant='borderless' className='px-2 rerun-copy-button' onClick={handleCopyClicked}>
          <i className='bi bi-copy' />
          <div className='paragraph-small'>Copy</div>
        </Button>
      ) : null}
      <div className='d-flex gap-2 flex-grow-1 justify-content-end'>
        <Button variant='secondary' onClick={onHide} disabled={isSaveLoading}>
          Cancel
        </Button>

        <SplitButton
          variant='primary'
          label='Replace'
          onClick={handleOverrideAiResult}
          loading={isSaveLoading}
          disabled={isRerunLoading || !newAiResult?.result}
        >
          <Button variant='primary' className='px-2 py-1 flex-grow-1 justify-content-start' onClick={handleSaveAiResult} disabled={isSaveLoading}>
            Save
          </Button>
        </SplitButton>
      </div>
    </div>
  )

  return <Modal size='xl' header={<></>} body={rerunModalBody} show={show} onHide={onHide} footer={rerunModalFooter} />
}
